/**
 * Ordering and de-duplication gate for live telemetry ticks.
 *
 * What this guarantees
 * --------------------
 * Ticks are released STRICTLY MONOTONICALLY by epoch (`E`). A tick whose epoch is
 * at or below the last released one is dropped. That makes ingest idempotent —
 * replaying a tick is harmless — which is what lets the fallback be "just poll
 * again" instead of something that must reason about what was already delivered.
 *
 * Why a monotonic gate and not a reordering buffer
 * ------------------------------------------------
 * SignalR already delivers in order within a connection, so on the happy path
 * there is nothing to reorder. Out-of-order arrivals are only possible where the
 * socket and the polling path overlap:
 *
 *   1. backfill -> live handoff,
 *   2. reconnect, where a catch-up poll runs alongside resumed broadcasts,
 *   3. flapping, where the two interleave.
 *
 * In every one of those cases the lower-epoch tick is one the poll has already
 * ingested, so dropping it is correct.
 *
 * A first version of this class buffered briefly to let a straggler slot in ahead
 * of a higher epoch. Its own tests showed that to be wrong twice over: it added
 * the full hold window of latency to EVERY tick on a stream that is already
 * ordered, and a straggler released after its successor would have been appended
 * out of position anyway, because the ring buffer is addressed by absolute tick.
 * A genuinely missing tick is recovered by the next catch-up poll, which resumes
 * from the family cursor and is therefore gap-free (item 2's HWM). So the gate
 * stays: no added latency, no buffer to bound, no way to strand a tick.
 */

/** Anything carrying a telemetry epoch. */
export interface EpochBearing {
  E?: number;
}

export class LiveTickSequencer<T extends EpochBearing> {
  /** Highest epoch released so far; everything at or below it is already ingested. */
  private lastReleased = 0;
  private droppedDuplicates = 0;
  private releasedCount = 0;

  /** Highest epoch released — mirrors what the poll cursor should be. */
  get highWaterMark(): number {
    return this.lastReleased;
  }
  get duplicatesDropped(): number {
    return this.droppedDuplicates;
  }
  get totalReleased(): number {
    return this.releasedCount;
  }

  /**
   * Seed from a known cursor — the backfill's HWM, or the family cursor after a
   * catch-up poll. Ticks at or below it are then treated as already seen. Never
   * moves backwards, so a stale seed cannot re-open the door to duplicates.
   */
  seed(epoch: number): void {
    if (Number.isFinite(epoch) && epoch > this.lastReleased) {
      this.lastReleased = epoch;
    }
  }

  /**
   * Offer a tick. Returns it when it advances the stream, or nothing when it is a
   * duplicate, stale, or malformed.
   */
  offer(tick: T | null | undefined): T[] {
    const epoch = Number(tick?.E);
    if (!tick || !Number.isFinite(epoch)) {
      return [];
    }
    if (epoch <= this.lastReleased) {
      this.droppedDuplicates++;
      return [];
    }
    this.lastReleased = epoch;
    this.releasedCount++;
    return [tick];
  }

  /**
   * Offer a batch. Sorted ascending first, so a payload carrying several ticks is
   * released in epoch order regardless of the order it arrived in.
   */
  offerBatch(ticks: readonly (T | null | undefined)[] | null | undefined): T[] {
    if (!ticks || ticks.length === 0) {
      return [];
    }
    const sorted = [...ticks]
      .filter((x): x is T => Number.isFinite(Number(x?.E)))
      .sort((a, b) => Number(a.E) - Number(b.E));

    const out: T[] = [];
    for (const tick of sorted) {
      out.push(...this.offer(tick));
    }
    return out;
  }

  /** Drop all state — project change. */
  reset(): void {
    this.lastReleased = 0;
    this.droppedDuplicates = 0;
    this.releasedCount = 0;
  }
}
